import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Brain,
  Moon,
  Palette,
  Play,
  ShieldCheck,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { ProfessionalsPanel } from "../ProfessionalsPanel";
import { SchoolPanel } from "../SchoolPanel";
import { AccessLogPanel } from "../AccessLogPanel";
import { WeekAndStories } from "./WeekAndStories";
import { ParentVoiceNote } from "./ParentVoiceNote";
import { ShapeAlbum } from "./ShapeAlbum";
import { api, errMessage } from "../../lib/api";
import { CHILD_THEMES } from "../../lib/childThemes";

const AVATAR_CLS = {
  amber: "bg-amber-400",
  sky: "bg-sky-400",
  emerald: "bg-emerald-400",
  rose: "bg-rose-400",
  violet: "bg-violet-400",
};

const PURPOSE_LABELS = {
  core_learning: "Core learning",
  progress_tracking: "Progress tracking",
  accessibility_personalization: "Accessibility",
  ai_story_generation: "AI stories & pictures",
  ai_companion_chat: "AI coach chat",
  voice_practice: "Voice practice",
};

const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? " am" : " pm"}`;

export const ChildCard = ({ child, notice, onChanged }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const grant = child.consent;
  const hasCore = grant?.status === "active" && grant?.purposes?.core_learning;

  const setPurpose = async (key, value) => {
    setBusy(true);
    try {
      const purposes = {
        ...(grant?.purposes || {}),
        core_learning: true,
        [key]: value,
      };
      await api.post("/consent/grants", {
        child_id: child.child_id,
        purposes,
        notice_version: notice.version,
        guardian_attestation: true,
      });
      toast.success("Consent updated");
      onChanged();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const revokeAll = async () => {
    setBusy(true);
    try {
      await api.post(`/consent/grants/${grant.grant_id}/revoke`);
      toast.success("All consent withdrawn. Child mode is now locked.");
      onChanged();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const regrant = async () => {
    setBusy(true);
    try {
      await api.post("/consent/grants", {
        child_id: child.child_id,
        purposes: {
          core_learning: true,
          progress_tracking: true,
          accessibility_personalization: true,
          ai_story_generation: false,
          ai_companion_chat: false,
          voice_practice: false,
        },
        notice_version: notice.version,
        guardian_attestation: true,
      });
      toast.success("Consent restored");
      onChanged();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteChild = async () => {
    try {
      const { data } = await api.delete(`/children/${child.child_id}`);
      toast.success(`Profile deleted. Deletion receipt: ${data.receipt_id}`);
      onChanged();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border p-6 space-y-5"
      data-testid={`child-card-${child.child_id}`}
    >
      <div className="flex items-center gap-4">
        <span
          className={`h-14 w-14 rounded-2xl ${AVATAR_CLS[child.avatar_color] || "bg-amber-400"} flex items-center justify-center text-xl font-extrabold text-white font-display`}
          aria-hidden="true"
        >
          {child.display_name.charAt(0).toUpperCase()}
        </span>
        <div className="flex-1">
          <h3
            className="text-lg font-bold font-display"
            data-testid="child-card-name"
          >
            {child.display_name}
          </h3>
          <p className="text-sm text-muted-foreground">Ages {child.age_band}</p>
          <p
            className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"
            data-testid="child-card-theme"
          >
            <Palette className="h-3 w-3" aria-hidden="true" />
            {
              (
                CHILD_THEMES.find((t) => t.key === (child.theme || "warm")) ||
                CHILD_THEMES[0]
              ).name
            }{" "}
            colors
          </p>
        </div>
        <Badge
          variant={hasCore ? "default" : "destructive"}
          data-testid="child-card-consent-badge"
        >
          {hasCore ? "Consent active" : "Consent withdrawn"}
        </Badge>
      </div>

      {child.quiet_hours?.enabled && (
        <p
          className="text-xs text-indigo-500 font-semibold flex items-center gap-1.5 rounded-lg bg-indigo-50/60 border border-indigo-100 px-3 py-1.5"
          data-testid="child-card-quiet-hours"
        >
          <Moon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Quiet hours tonight: {hourLabel(child.quiet_hours.start)} –{" "}
          {hourLabel(child.quiet_hours.end)}
        </p>
      )}

      {child.interests?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {child.interests.slice(0, 5).map((i) => (
            <span
              key={i}
              className="text-xs rounded-full bg-accent px-2.5 py-1 font-medium"
            >
              {i}
            </span>
          ))}
        </div>
      )}

      <Button
        className="w-full h-12 rounded-xl gap-2 text-base"
        disabled={!hasCore}
        onClick={() => navigate(`/child/${child.child_id}`)}
        data-testid="enter-child-mode-button"
      >
        <Play className="h-5 w-5" aria-hidden="true" /> Enter child mode
      </Button>
      <ShapeAlbum child={child} />
      {!hasCore && (
        <p className="text-xs text-muted-foreground -mt-2">
          Child mode is locked until consent is granted again.
        </p>
      )}

      <details className="rounded-xl border border-border">
        <summary
          className="cursor-pointer p-4 text-sm font-semibold flex items-center gap-2 list-none"
          data-testid="child-card-consent-summary"
        >
          <ShieldCheck className="h-4 w-4 text-secondary" aria-hidden="true" />{" "}
          Manage consent
        </summary>
        <div className="p-4 pt-0 space-y-3">
          {hasCore && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-accent/60 px-3 py-2">
              <div>
                <span className="text-sm">Marketplace activities</span>
                <p className="text-xs text-muted-foreground">
                  Fully-reviewed creator games in {child.display_name}'s menu
                  (pilot)
                </p>
              </div>
              <Switch
                checked={Boolean(child.marketplace_opt_in?.enabled)}
                disabled={busy}
                onCheckedChange={async (v) => {
                  setBusy(true);
                  try {
                    await api.put(`/children/${child.child_id}/marketplace`, {
                      enabled: Boolean(v),
                    });
                    toast.success(
                      v
                        ? "Marketplace activities on for this child"
                        : "Marketplace activities off",
                    );
                    onChanged();
                  } catch (e) {
                    toast.error(errMessage(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                aria-label="Marketplace activities"
                data-testid={`child-marketplace-toggle-${child.child_id}`}
              />
            </div>
          )}
          {hasCore ? (
            <>
              {Object.entries(PURPOSE_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="text-sm">{label}</span>
                  {key === "core_learning" ? (
                    <Badge
                      variant="secondary"
                      className="shrink-0"
                      data-testid="child-consent-core-always-on"
                    >
                      Always on
                    </Badge>
                  ) : (
                    <Switch
                      checked={Boolean(grant?.purposes?.[key])}
                      disabled={busy}
                      onCheckedChange={(v) => setPurpose(key, Boolean(v))}
                      aria-label={label}
                      data-testid={`child-consent-toggle-${key}`}
                    />
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full rounded-lg text-destructive border-destructive/40 hover:bg-destructive/10"
                disabled={busy}
                onClick={revokeAll}
                data-testid="revoke-all-consent-button"
              >
                Withdraw all consent
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full rounded-lg"
              disabled={busy}
              onClick={regrant}
              data-testid="regrant-consent-button"
            >
              Grant consent again
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full rounded-lg text-muted-foreground gap-2"
                data-testid="delete-child-button"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete profile
                & data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-child-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete {child.display_name}'s profile?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes the profile and all activity events,
                  and issues a deletion receipt. Consent records are kept as a
                  legal record, marked revoked.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-child-cancel">
                  Keep profile
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={deleteChild}
                  className="bg-destructive hover:bg-destructive/90"
                  data-testid="delete-child-confirm"
                >
                  Delete permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </details>

      <WeekAndStories child={child} />

      <ParentVoiceNote child={child} />

      <div className="grid grid-cols-2 gap-2">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="rounded-lg gap-2 text-muted-foreground"
          data-testid="growth-dashboard-link"
        >
          <Link to={`/growth/${child.child_id}`}>
            <TrendingUp className="h-4 w-4" aria-hidden="true" /> Growth
          </Link>
        </Button>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="rounded-lg gap-2 text-muted-foreground"
          data-testid="memory-console-link"
        >
          <Link to={`/memory/${child.child_id}`}>
            <Brain className="h-4 w-4" aria-hidden="true" /> Memory
          </Link>
        </Button>
      </div>
      <ProfessionalsPanel child={child} />
      <SchoolPanel child={child} />
      <AccessLogPanel child={child} />
    </div>
  );
};
